import React, { useState } from "react";
import StatusMonthYearSelector from "../key Register Change stmt/StatusMonthYearSelector";
import ReportTable from "../key Register Change stmt/ReportTable";
import { exportStyledExcel } from "../../LoginExcelDownLoads/AuaAuthAnalysis/exportStyledExcel";
import {
  getRcmsMemberDistrictWiseReport,
  getRcmsMemberBlockWiseReport,
  getRcmsMemberFpsWiseReport,
  downloadRcmsMemberDistrictWiseExcel,
  downloadRcmsMemberBlockWiseExcel,
  downloadRcmsMemberFpsWiseExcel,
  downloadRcmsMemberRawExcel,
} from "../../Service/api";

const getMonthName = (monthNumber) => {
  const date = new Date();
  date.setMonth(parseInt(monthNumber, 10) - 1);
  return date.toLocaleString("en-IN", { month: "long" });
};

const formatDate = (isoString) => {
  if (!isoString) return "";
  const date = new Date(isoString);
  if (isNaN(date.getTime())) return isoString;
  const pad = (num) => String(num).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
    date.getDate()
  )} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(
    date.getSeconds()
  )}`;
};

function RcmsMemberChangeStatementReport() {
  const [reportData, setReportData] = useState(null);
  const [blockReportData, setBlockReportData] = useState(null);
  const [fpsReportData, setFpsReportData] = useState(null);

  const [selectedDistrict, setSelectedDistrict] = useState(null);
  const [selectedBlock, setSelectedBlock] = useState(null);

  const [currentMonth, setCurrentMonth] = useState("");
  const [currentYear, setCurrentYear] = useState("");
  const [currentStatus, setCurrentStatus] = useState("");

  const [currentMonthName, setCurrentMonthName] = useState("");
  const [currentBlockName, setCurrentBlockName] = useState("");

  const [loading, setLoading] = useState(false);
  const [blockLoading, setBlockLoading] = useState(false);
  const [fpsLoading, setFpsLoading] = useState(false);

  const [error, setError] = useState(null);
  const [blockError, setBlockError] = useState(null);
  const [fpsError, setFpsError] = useState(null);

  const fetchReport = async (month, year, status) => {
    setLoading(true);
    setError(null);
    setReportData(null);
    setBlockReportData(null);
    setFpsReportData(null);
    setSelectedDistrict(null);
    setSelectedBlock(null);

    setCurrentMonth(month);
    setCurrentYear(year);
    setCurrentStatus(status);
    setCurrentMonthName(getMonthName(month));
    setCurrentBlockName("");

    try {
      const data = await getRcmsMemberDistrictWiseReport(month, year, status);
      setReportData(data);
    } catch (err) {
      setError("Failed to fetch district wise report.");
    } finally {
      setLoading(false);
    }
  };

  const fetchBlockReport = async (distCode, districtName) => {
    setBlockLoading(true);
    setBlockError(null);
    setBlockReportData(null);
    setFpsReportData(null);
    setSelectedDistrict(distCode);
    setSelectedBlock(null);
    setCurrentBlockName(districtName);

    try {
      const data = await getRcmsMemberBlockWiseReport(
        distCode,
        currentMonth,
        currentYear,
        currentStatus
      );
      setBlockReportData(data);
    } catch (err) {
      setBlockError("Failed to fetch block wise report.");
    } finally {
      setBlockLoading(false);
    }
  };

  const fetchFpsReport = async (distCode, officeCode, blockName) => {
    setFpsLoading(true);
    setFpsError(null);
    setFpsReportData(null);
    setSelectedBlock(officeCode);
    setCurrentBlockName(blockName);

    try {
      const data = await getRcmsMemberFpsWiseReport(
        distCode,
        officeCode,
        currentMonth,
        currentYear,
        currentStatus
      );
      setFpsReportData(data);
    } catch (err) {
      setFpsError("Failed to fetch FPS wise report.");
    } finally {
      setFpsLoading(false);
    }
  };

  const handleDownloadDistrictReport = async () => {
    try {
      const data = await downloadRcmsMemberDistrictWiseExcel(
        currentMonth,
        currentYear,
        currentStatus
      );
      if (data && data.length > 0) {
        const formattedData = data.map((item) => ({
          "Sl No": item.slNo,
          "District Name": item.districtName,
          "District Code": item.distCode,
          "Total Members": item.totalCards,
          Addition: item.addition,
          Modification: item.modification,
          Deletion: item.deletion,
        }));
        const fileName = `RCMS_Member_District_Report_${currentStatus}_${currentMonth}_${currentYear}.xlsx`;
        const title = `RCMS Member District Report (${currentStatus}) - ${currentMonthName} ${currentYear}`;
        await exportStyledExcel(formattedData, fileName, title);
      } else {
        alert("No data found");
      }
    } catch (err) {
      console.error(err);
      alert("Failed to download");
    }
  };

  const handleDownloadBlockReport = async () => {
    if (selectedDistrict) {
      try {
        const data = await downloadRcmsMemberBlockWiseExcel(
          selectedDistrict,
          currentMonth,
          currentYear,
          currentStatus
        );
        if (data && data.length > 0) {
          const formattedData = data.map((item) => ({
            "Sl No": item.slNo,
            "Block Name": item.blockName,
            "Office Code": item.officeCode,
            "Total Members": item.totalCards,
            Addition: item.addition,
            Modification: item.modification,
            Deletion: item.deletion,
          }));
          const fileName = `RCMS_Member_Block_Report_${selectedDistrict}_${currentMonth}_${currentYear}.xlsx`;
          const title = `RCMS Member Block Report (${currentStatus}) - ${currentMonthName} ${currentYear}`;
          await exportStyledExcel(formattedData, fileName, title);
        } else {
          alert("No data found");
        }
      } catch (err) {
        console.error(err);
        alert("Failed to download");
      }
    }
  };

  const handleDownloadFpsReport = async () => {
    if (selectedDistrict && selectedBlock) {
      try {
        const data = await downloadRcmsMemberFpsWiseExcel(
          selectedDistrict,
          selectedBlock,
          currentMonth,
          currentYear,
          currentStatus
        );
        if (data && data.length > 0) {
          const formattedData = data.map((item, index) => ({
            "Sl No": index + 1,
            "FPS ID": item.fpsId,
            "RC ID": item.rcId,
            "Member Name": item.memberName,
            "Member ID": item.memberId,
            Gender: item.gender,
            Age: item.age,
            Relation: item.relation,
            "Change Status": item.changeStatus,
          }));
          const fileName = `RCMS_Member_FPS_Report_${selectedBlock}_${currentMonth}_${currentYear}.xlsx`;
          const title = `RCMS Member FPS Report (${currentStatus}) - ${currentMonthName} ${currentYear}`;
          await exportStyledExcel(formattedData, fileName, title);
        } else {
          alert("No data found");
        }
      } catch (err) {
        console.error(err);
        alert("Failed to download");
      }
    }
  };

  const handleRawDownload = async (distCode, officeCode, changeType) => {
    try {
      const data = await downloadRcmsMemberRawExcel(
        distCode,
        officeCode,
        currentMonth,
        currentYear,
        currentStatus
      );

      if (data && data.length > 0) {
        let filteredData = data;
        let headingPrefix = "RCMS Member Full Data";

        if (changeType === "A") {
          filteredData = data.filter((item) => item.changeStatus === "A");
          headingPrefix = "RCMS Member Addition Data";
        } else if (changeType === "M") {
          filteredData = data.filter((item) => item.changeStatus === "M");
          headingPrefix = "RCMS Member Modification Data";
        } else if (changeType === "D") {
          filteredData = data.filter((item) => item.changeStatus === "D");
          headingPrefix = "RCMS Member Deletion Data";
        }

        if (filteredData.length === 0) {
          alert(`No data found for ${headingPrefix}`);
          return;
        }

        const formattedData = filteredData.map((item, index) => ({
          "Sl No": index + 1,
          District: item.districtName,
          Block: item.blockName,
          "FPS ID": item.fpsId,
          "RC ID": item.rcId,
          "Member Name": item.memberName,
          "Member ID": item.memberId,
          Gender: item.gender,
          Age: item.age,
          Relation: item.relation,
          UID: item.uid,
          "Updated Date": formatDate(item.updatedTime),
          "Change Status": item.changeStatus,
        }));

        let statusLabel = currentStatus;
        if (currentStatus === "Y") statusLabel = "Applied (Y)";
        else if (currentStatus === "N") statusLabel = "Not Applied (N)";
        else if (currentStatus === "F") statusLabel = "Failed (F)";

        const fileName = `${headingPrefix.replace(
          /\s/g,
          "_"
        )}_${currentStatus}_${currentMonth}_${currentYear}.xlsx`;
        const title = `${headingPrefix} - ${statusLabel} - ${currentMonthName} ${currentYear}`;
        await exportStyledExcel(formattedData, fileName, title);
      } else {
        alert("No data found for raw export");
      }
    } catch (err) {
      console.error("Raw download error:", err);
      alert("Failed to download Raw Data");
    }
  };

  const handleTotalClick_StateLevel = async (type) => {
    await handleRawDownload(null, null, type);
  };
  const handleTotalClick_DistrictLevel = async (type) => {
    if (selectedDistrict) await handleRawDownload(selectedDistrict, null, type);
  };
  const handleTotalClick_BlockLevel = async (type) => {
    if (selectedDistrict && selectedBlock)
      await handleRawDownload(selectedDistrict, selectedBlock, type);
  };

  return (
    <div className="py-6">
      <div className="">
        <StatusMonthYearSelector
          onSubmit={fetchReport}
          heading="RCMS Member Details Change Statement"
        />
      </div>

      {loading && (
        <p className="text-center mt-6 text-blue-600 text-lg font-medium">
          Loading district report...
        </p>
      )}
      {error && (
        <p className="text-center mt-6 text-red-600 text-lg font-medium">
          Error: {error}
        </p>
      )}

      {reportData && !loading && !error && (
        <div className="py-6">
          <ReportTable
            data={reportData}
            mainTitle="RCMS Member Change Statement Report"
            totalLabel="Total Members"
            onDistrictClick={fetchBlockReport}
            onDownloadReport={handleDownloadDistrictReport}
            onTotalClick={handleTotalClick_StateLevel}
            monthName={currentMonthName}
            year={currentYear}
            blockName={""}
          />
        </div>
      )}

      {selectedDistrict && (
        <div className="">
          {blockLoading && (
            <p className="text-center text-blue-600 text-lg font-medium">
              Loading block report...
            </p>
          )}
          {blockError && (
            <p className="text-center text-red-600 text-lg font-medium">
              Error: {blockError}
            </p>
          )}
          {blockReportData && !blockLoading && !blockError && (
            <>
              <ReportTable
                data={blockReportData}
                mainTitle="RCMS Member Change Statement Report"
                totalLabel="Total Members"
                onBlockClick={fetchFpsReport}
                isBlockTable={true}
                onDownloadReport={handleDownloadBlockReport}
                onTotalClick={handleTotalClick_DistrictLevel}
                monthName={currentMonthName}
                year={currentYear}
                blockName={currentBlockName}
              />

              {selectedBlock && (
                <div className="py-6">
                  {fpsLoading && (
                    <p className="text-center text-blue-600 text-lg font-medium">
                      Loading FPS report...
                    </p>
                  )}
                  {fpsError && (
                    <p className="text-center text-red-600 text-lg font-medium">
                      Error: {fpsError}
                    </p>
                  )}
                  {fpsReportData && !fpsLoading && !fpsError && (
                    <ReportTable
                      data={fpsReportData}
                      mainTitle="RCMS Member Change Statement Report"
                      totalLabel="Total Members"
                      isFpsTable={true}
                      onDownloadReport={handleDownloadFpsReport}
                      onTotalClick={handleTotalClick_BlockLevel}
                      monthName={currentMonthName}
                      year={currentYear}
                      blockName={currentBlockName}
                    />
                  )}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default RcmsMemberChangeStatementReport;
